import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ShieldCheck, CheckCircle2, ArrowRight, AlertTriangle, Lock, Eye, FileText, Server } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const jakarta = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const mono = { fontFamily: "'JetBrains Mono', monospace" };

const CTA = ({ to = "/start", children, testid, variant = "primary", size = "lg", className = "" }) => (
  <Link to={to} data-testid={testid}>
    <Button size={size} className={variant === "primary"
      ? `group bg-emerald-600 font-bold shadow-lg shadow-emerald-600/20 hover:bg-emerald-700 ${className}`
      : `border-emerald-300 font-semibold text-emerald-800 hover:bg-emerald-50 ${className}`}
      variant={variant === "primary" ? "default" : "outline"}>
      {children} <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
    </Button>
  </Link>
);

const Header = () => (
  <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-md">
    <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-600 shadow-sm">
          <ShieldCheck className="h-5 w-5 text-white" />
        </div>
        <div>
          <div className="text-sm font-extrabold tracking-tight text-slate-900" style={jakarta}>AuditŠVP</div>
          <div className="hidden text-[10px] uppercase tracking-widest text-slate-400 md:block" style={mono}>Kontrola hotového ŠVP podle nového RVP PV</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Link to="/login" data-testid="landing-login-link" className="hidden sm:block">
          <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900">Přihlásit se</Button>
        </Link>
        <Link to="/start" data-testid="nav-start-audit-button">
          <Button size="sm" className="bg-emerald-600 font-semibold shadow-md shadow-emerald-600/20 hover:bg-emerald-700">
            Prověřit moje ŠVP zdarma
          </Button>
        </Link>
      </div>
    </div>
  </header>
);

const StickyCta = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const fn = () => setShow(window.scrollY > 700);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  if (!show) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-4px_20px_rgba(15,23,42,0.08)] backdrop-blur" data-testid="sticky-cta-bar">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
        <span className="text-sm font-bold text-slate-900" style={jakarta}>Máte ŠVP hotové?</span>
        <Link to="/start" data-testid="sticky-cta-button">
          <Button size="sm" className="bg-emerald-600 font-semibold hover:bg-emerald-700">Prověřit zdarma <ArrowRight className="ml-1.5 h-3.5 w-3.5" /></Button>
        </Link>
      </div>
    </div>
  );
};

const EvidenceMock = () => (
  <div className="relative mx-auto mt-12 max-w-4xl" data-testid="severity-sample-card">
    <div className="absolute -inset-3 -z-10 rounded-3xl bg-gradient-to-br from-emerald-200/50 via-transparent to-slate-200/60 blur-xl" />
    <div className="overflow-hidden rounded-2xl border border-slate-300 bg-white text-left shadow-2xl shadow-slate-300/50">
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-5 py-2.5">
        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500" style={mono}>Výsledek kontroly vašeho ŠVP</span>
        <span className="rounded bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800" style={mono}>664 kontrol dokončeno</span>
      </div>
      <div className="grid grid-cols-4 divide-x divide-slate-100 border-b border-slate-100 text-center">
        {[["5", "kritických", "text-red-700"], ["8", "závažných", "text-amber-700"], ["14", "středních", "text-blue-700"], ["6", "k ověření", "text-slate-400"]].map(([n, l, c]) => (
          <div key={l} className="py-4">
            <div className={`text-2xl font-extrabold ${c}`} style={jakarta}>{n}</div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400" style={mono}>{l}</div>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-[55fr_45fr] md:divide-x md:divide-slate-100">
        <div className="p-6">
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-red-700" style={mono}>Kritické zjištění · Pedagogické diagnostikování</span>
          </div>
          <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-slate-400" style={mono}>Vaše ŠVP · strana 22</div>
          <p className="mt-1.5 rounded-lg bg-slate-50 p-3 text-sm italic leading-relaxed text-slate-600">
            „…sledujeme <mark className="rounded bg-yellow-200 px-0.5">pokroky dětí průběžně při běžných činnostech</mark>, poznatky si předáváme ústně na poradách…“
          </p>
          <p className="mt-3 text-sm leading-relaxed text-slate-700">Sledování dítěte není popsáno jako ucelený systém, jak vyžaduje nový RVP PV.</p>
          <div className="mt-3 flex flex-wrap gap-1.5 text-[10px]" style={mono}>
            <span className="rounded bg-slate-900 px-1.5 py-0.5 font-bold text-white">POVINNÉ</span>
            <span className="rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-800">splněno jen částečně</span>
            <span className="rounded bg-red-100 px-1.5 py-0.5 font-semibold text-red-800">závažnost 9/10</span>
          </div>
        </div>
        <div className="border-t border-slate-100 p-6 md:border-t-0">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400" style={mono}>Pokrytí vzdělávacích oblastí</div>
          <div className="mt-3 space-y-2.5">
            {[["Dítě a jeho tělo", 92, "bg-emerald-500"], ["Dítě a jeho psychika", 78, "bg-emerald-500"], ["Dítě, ten druhý a společnost", 64, "bg-amber-500"], ["Dítě a svět", 41, "bg-red-500"]].map(([n, v, c]) => (
              <div key={n}>
                <div className="flex justify-between text-[11px] text-slate-600"><span>{n}</span><span className="font-bold" style={mono}>{v} %</span></div>
                <div className="mt-1 h-1.5 rounded-full bg-slate-100"><div className={`h-1.5 rounded-full ${c}`} style={{ width: `${v}%` }} /></div>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-1.5 text-[11px] text-slate-500">
            {[["Nadané děti — systém podpory", "str. 17"], ["Jazyková podpora dětí s OMJ", "nedoloženo"]].map(([t, p]) => (
              <div key={t} className="flex items-center justify-between rounded-lg border border-slate-100 px-2.5 py-1.5">
                <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" />{t}</span>
                <span className="font-semibold text-slate-400" style={mono}>{p}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50 px-6 py-2.5 text-[11px] text-slate-500">
        <span>+ 24 dalších zjištění s citacemi z vašeho dokumentu · export PDF a Excel</span>
        <Lock className="h-3.5 w-3.5" />
      </div>
    </div>
  </div>
);

export default function LandingPage() {
  const { user } = useAuth();
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-[0.4]" style={{ backgroundImage: "linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
        <div className="relative mx-auto max-w-4xl px-4 pb-24 pt-20 text-center sm:px-6 lg:pt-28">
          <h1 className="text-4xl font-extrabold leading-[1.25] tracking-tight sm:text-5xl lg:text-6xl" style={jakarta}>
            Máte ŠVP hotové?<br />
            <span className="text-emerald-600">Teď zjistěte, jestli je opravdu v pořádku.</span>
          </h1>
          <p className="mx-auto mt-8 max-w-2xl text-lg leading-relaxed text-slate-600">
            Prošli jste ho několikrát. Upravili podle nového RVP PV. Kolegyně to potvrdily.<br className="hidden sm:block" />
            A přesně teď přichází ta nejdůležitější otázka: <b className="text-slate-900">nepřehlédli jste něco?</b>
          </p>
          <p className="mx-auto mt-5 max-w-2xl text-base text-slate-600">
            AuditŠVP projde váš dokument podle <b className="text-slate-900">600+ kontrolních bodů</b> nového RVP PV.
          </p>
          <div className="mt-9 flex flex-col items-center gap-3">
            <CTA testid="hero-start-audit-button" className="h-14 px-6 text-base sm:h-16 sm:px-12 sm:text-xl">Prověřit moje ŠVP zdarma</CTA>
            <span className="text-xs text-slate-500" style={mono}>Bez karty · výsledek za pár minut</span>
          </div>
          <p className="mx-auto mt-8 max-w-2xl rounded-xl border border-emerald-200 bg-emerald-50/70 px-5 py-3.5 text-sm leading-relaxed text-slate-700">
            Zdarma zjistíte, kolik zjištění AuditŠVP našel a jak jsou závažná. V plné verzi pak u každého uvidíte
            přesné místo ve vašem ŠVP, citaci z dokumentu a vysvětlení, co je potřeba upravit.
          </p>
        </div>
      </section>

      {/* DŮKAZ */}
      <section className="border-y border-slate-200 bg-white py-24">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl" style={jakarta}>Takto vypadá výsledek plného auditu.</h2>
          <EvidenceMock />
          <div className="mt-8 text-center">
            <Link to="/ukazka" data-testid="landing-demo-audit-link">
              <Button variant="outline" className="h-14 border-2 border-emerald-600 px-8 text-base font-bold text-emerald-700 hover:bg-emerald-50">
                Prohlédnout celý ukázkový audit
              </Button>
            </Link>
            <p className="mt-2 text-xs text-slate-500">Skutečný kompletní audit fiktivní mateřské školy — bez registrace, jen se podívejte.</p>
          </div>
          <p className="mx-auto mt-8 max-w-2xl text-sm leading-relaxed text-slate-600">
            V plné verzi u každého zjištění uvidíte konkrétní stránku, citaci z vašeho ŠVP a vysvětlení, v čem je problém.
            <b className="text-slate-900"> Žádné obecné hodnocení — vždy přesně víte, které místo ve vašem dokumentu je potřeba řešit.</b>
          </p>
          <div className="mt-7"><CTA testid="proof-cta-button" className="h-14 px-6 text-base sm:h-16 sm:px-12 sm:text-xl">Ukázat mi, co najde v mém ŠVP</CTA></div>
        </div>
      </section>

      {/* PROČ SE TO STÁVÁ */}
      <section className="py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <h2 className="text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl" style={jakarta}>
            Do ŠVP jste dali spoustu času a úsilí. <span className="text-emerald-600">Teď zjistěte, jestli je v souladu s novým RVP PV.</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-slate-700">
            AuditŠVP je <b>druhý pár očí, než řeknete „máme hotovo“</b> — kontrola konkrétně vašeho textu proti 600+ požadavkům.
          </p>
          <p className="mt-4 text-base leading-relaxed text-slate-600">
            Školení a metodiky vám ukážou, co nový RVP PV požaduje a co je potřeba změnit.
            Neřeknou vám ale, kde konkrétně je problém právě ve vašem ŠVP.
          </p>
          <p className="mt-4 text-sm text-slate-500">
            (Nemá smysl u rozpracovaného dokumentu — je pro ŠVP, které je hotové nebo těsně před uzavřením.)
          </p>
        </div>
      </section>

      {/* JAK TO FUNGUJE */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-xs font-bold uppercase tracking-widest text-emerald-700" style={mono}>Jak to funguje</div>
          <div className="mt-8 grid gap-8 md:grid-cols-3">
            {[["01", "Nahrajete svůj stávající ŠVP", "PDF nebo DOCX"],
              ["02", "Projdeme 600+ kontrol", "Podle požadavků nového RVP PV."],
              ["03", "Víte, na čem jste", "Kolik zjištění máte a jak závažná, uvidíte hned. Kde přesně a proč ukáže plná verze."]].map(([n, t, d]) => (
              <div key={n} className="group rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition-all hover:-translate-y-1 hover:border-emerald-300 hover:shadow-lg">
                <div className="text-4xl font-extrabold text-slate-200 transition-colors group-hover:text-emerald-200" style={mono}>{n}</div>
                <h3 className="mt-3 text-lg font-bold" style={jakarta}>{t}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CENA */}
      <section className="border-y border-slate-200 bg-white py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl" style={jakarta}>Zdarma zjistíte, jak na tom vaše ŠVP je. V plné verzi uvidíte, co přesně upravit.</h2>
            <p className="mt-3 text-sm text-slate-600">Počet zjištění vám dá představu. <b className="text-slate-900">Přesné místo a vysvětlení vám dají jistotu.</b></p>
          </div>
          <div className="mx-auto mt-10 grid max-w-4xl items-stretch gap-8 lg:grid-cols-2">
            <div className="flex flex-col rounded-2xl border border-slate-200 bg-slate-50/60 p-8">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-400" style={mono}>Zdarma</div>
              <div className="mt-2 text-3xl font-extrabold" style={jakarta}>0 Kč</div>
              <ul className="mt-5 flex-1 space-y-2.5 text-sm text-slate-600">
                {["Počet zjištění", "Rozdělení podle závažnosti", "Orientační pokrytí"].map((t) => (
                  <li key={t} className="flex gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" /> {t}</li>
                ))}
              </ul>
            </div>
            <div className="relative flex flex-col rounded-2xl border-2 border-emerald-500 bg-white p-8 shadow-xl shadow-emerald-600/10">
              <span className="absolute -top-3.5 left-6 rounded-full bg-emerald-600 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white" style={mono}>Plný přístup</span>
              <div className="text-xs font-bold uppercase tracking-widest text-emerald-700" style={mono}>Přesně víte, co upravit</div>
              <div className="mt-2 text-3xl font-extrabold" style={jakarta}>1 990 Kč <span className="text-sm font-medium text-slate-400">jednorázově</span></div>
              <ul className="mt-5 flex-1 space-y-2.5 text-sm text-slate-700">
                {["U každého zjištění stránka a citace z vašeho ŠVP", "Vysvětlení, proč dané místo neodpovídá novému RVP PV", "Přehled, jak vaše ŠVP pokrývá všechny vzdělávací oblasti", "Výsledky ke stažení v PDF a Excelu", "5 kontrol v ceně, třeba i po úpravách ŠVP"].map((t) => (
                  <li key={t} className="flex gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" /> {t}</li>
                ))}
              </ul>
              <Link to="/start" data-testid="pricing-paid-plan-button">
                <Button className="mt-7 w-full bg-emerald-600 font-bold shadow-md shadow-emerald-600/20 hover:bg-emerald-700">
                  Zjistit, co přesně upravit <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <p className="mt-2 text-center text-xs text-slate-400" style={mono}>Žádné předplatné · jedna platba</p>
            </div>
          </div>
        </div>
      </section>

      {/* NÁMITKY */}
      <section className="py-24">
        <div className="mx-auto grid max-w-5xl gap-8 px-4 sm:px-6 md:grid-cols-3">
          {[["„Už jsme to kontrolovali.“", "Tím líp. Audit vaše ŠVP porovná s 600+ požadavky nového RVP PV a buď potvrdí, že je vše v pořádku, nebo ukáže, co doplnit."],
            ["„Nechci, aby mi AI přepisovala ŠVP.“", "Nepřepisuje. Ukáže, co neodpovídá RVP PV a proč. Jak to upravíte, je jen na vás."],
            ["„Co když se AI splete?“", "U každého zjištění vidíte stránku a citaci z vašeho ŠVP. Vše si snadno ověříte sami."]].map(([q, a]) => (
            <div key={q} className="rounded-2xl border border-slate-200 bg-white p-7 transition-colors hover:border-emerald-300">
              <h3 className="text-base font-bold text-slate-900" style={jakarta}>{q}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BEZPEČNOST */}
      <section className="py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <div className="text-xs font-bold uppercase tracking-widest text-emerald-700" style={mono}>Bezpečnost dokumentů</div>
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[[Server, "Vše šifrovaně", "Vaše ŠVP přenášíme i zpracováváme šifrovaně a používáme ho jen pro váš audit."],
              [Eye, "Přístup jen pro vaši školu", "Dokument i výsledky vidíte jen vy. Nikdo jiný k nim přístup nemá."],
              [FileText, "Žádné trénování AI", "Vaše ŠVP nikdy nepoužijeme k trénování umělé inteligence."],
              [ShieldCheck, "Smazání na požádání", "Data zpracováváme podle GDPR. Dokument i výsledky smažeme, kdykoli o to požádáte."]].map(([Icon, t, d]) => (
              <div key={t} className="rounded-2xl border border-slate-200 bg-white p-6">
                <Icon className="h-5 w-5 text-emerald-600" />
                <h3 className="mt-3 text-sm font-bold" style={jakarta}>{t}</h3>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-slate-200 bg-white py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <h2 className="text-center text-3xl font-extrabold tracking-tight" style={jakarta}>Časté dotazy</h2>
          <div className="mt-8 space-y-3">
            {[["Pro jaké ŠVP je audit určený?", "Pro hotové nebo těsně před uzavřením. U rozpracovaného dokumentu audit logicky označí, co v něm zatím chybí."],
              ["Nahrazuje AuditŠVP ČŠI?", "Ne — je to podpůrný nástroj, nenahrazuje stanovisko MŠMT, NPI ani ČŠI."],
              ["Musím zadat kartu?", "Ne. Bezplatnou kontrolu spustíte bez zadání karty a plný přístup platíte na fakturu."],
              ["Proč ověření telefonem?", "Ochrana bezplatné služby proti zneužití, trvá pár sekund."],
              ["Kolikrát můžu audit použít?", "V ceně plného přístupu máte 5 kontrol. Můžete tak ŠVP po úpravách znovu zkontrolovat, nebo prověřit další dokumenty."],
              ["V jakém formátu mám ŠVP nahrát?", "Stačí PDF nebo dokument z Wordu (DOCX). Nijak ho předem upravovat nemusíte."]].map(([q, a]) => (
              <details key={q} className="group rounded-xl border border-slate-200 bg-slate-50/50 px-5 py-4 transition-colors hover:border-emerald-300" data-testid="faq-item-accordion">
                <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-bold text-slate-900">
                  {q} <span className="text-emerald-600 transition-transform group-open:rotate-45" style={mono}>+</span>
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ZÁVĚR */}
      <section className="px-4 py-24 sm:px-6">
        <div className="mx-auto max-w-4xl overflow-hidden rounded-3xl bg-slate-900 px-8 py-16 text-center text-white">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl" style={jakarta}>
            ŠVP máte hotové. <span className="text-emerald-400">Dejte mu ještě jednu kontrolu.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-300">
            Nahrajte dokument a zjistěte za pár minut, kolik zjištění obsahuje. Přesnou diagnózu — kde a proč — pak odemknete v plné verzi.
          </p>
          <Link to="/start" data-testid="footer-banner-start-button">
            <Button size="lg" className="mt-8 bg-emerald-500 px-10 text-base font-bold text-slate-950 shadow-lg shadow-emerald-500/25 hover:bg-emerald-400">
              Prověřit moje ŠVP zdarma <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <p className="mt-3 text-xs text-slate-400" style={mono}>0 Kč · bez karty · výsledek za pár minut</p>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white py-10 pb-20">
        <div className="mx-auto max-w-7xl px-4 text-center text-[11px] leading-relaxed text-slate-400 sm:px-6 lg:px-8">
          <p>© {new Date().getFullYear()} AuditŠVP · JustRight Stage s.r.o. · IČO: 08588023</p>
          <p className="mt-1">Podpůrný diagnostický nástroj — nenahrazuje stanovisko MŠMT, NPI ani ČŠI.</p>
        </div>
      </footer>

      <StickyCta />
    </div>
  );
}
